import React from 'react';
import { Share2, Twitter, Linkedin } from 'lucide-react';
export function Manifesto() {
  const principles = [{
    title: 'Capability is the fundamental unit',
    description: 'Skills, knowledge, and experience determine what an organisation can deliver. Not roles. Not titles. Not headcount.'
  }, {
    title: 'Teams exist to serve a purpose',
    description: "A team's function defines what capabilities it needs. People should be assembled to meet that purpose, not retrofitted into roles that existed before the work did."
  }, {
    title: 'People are not interchangeable',
    description: 'Each person is a unique combination of skills, experience, depth, and potential. Treating people as fungible resources destroys effectiveness and burns trust.'
  }, {
    title: 'Design before you hire',
    description: 'Hiring without understanding the capabilities you actually need is guesswork. Define the gap first. Then decide whether to hire, train, restructure, or automate.'
  }, {
    title: 'Change through recomposition, not reorganisation',
    description: "Most change should come from evolving capability and team composition, not constant structural upheaval. If you're reorganising every year, you're not designing — you're reacting."
  }, {
    title: 'Constraints shape the design',
    description: "Regulation, cognitive load, system boundaries, risk tolerance — these aren't obstacles to work around. They're inputs that should actively shape how teams are built."
  }];
  return <section id="manifesto" className="py-16 md:py-24 bg-slate-900 text-white" data-id="element-207">
      <div className="container mx-auto px-4 max-w-5xl" data-id="element-208">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-16 gap-6" data-id="element-209">
          <div data-id="element-210">
            <h2 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-211">
              The ATD Manifesto
            </h2>
            <p className="text-slate-400 text-lg max-w-xl" data-id="element-212">
              Core principles that guide science-inspired organisational design.
            </p>
          </div>
          <div className="flex gap-3" data-id="element-213">
            <button className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm font-medium transition-colors" data-id="element-214">
              <Share2 className="h-4 w-4" data-id="element-215" />
              <span data-id="element-216">Share</span>
            </button>
            <button className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors" aria-label="Share on Twitter" data-id="element-217">
              <Twitter className="h-4 w-4" data-id="element-218" />
            </button>
            <button className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors" aria-label="Share on LinkedIn" data-id="element-219">
              <Linkedin className="h-4 w-4" data-id="element-220" />
            </button>
          </div>
        </div>

        <div className="grid gap-8" data-id="element-221">
          {principles.map((principle, index) => <div key={index} className="group flex gap-6 p-6 rounded-2xl hover:bg-slate-800/50 transition-colors border border-transparent hover:border-slate-700" data-id="element-222">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-indigo-500/10 text-indigo-400 flex items-center justify-center font-bold text-xl border border-indigo-500/20 group-hover:bg-indigo-500 group-hover:text-white transition-all" data-id="element-223">
                {index + 1}
              </div>
              <div data-id="element-224">
                <h3 className="text-2xl md:text-3xl font-bold mb-3 text-slate-100" data-id="element-225">
                  {principle.title}
                </h3>
                <p className="text-slate-400 text-lg leading-relaxed" data-id="element-226">
                  {principle.description}
                </p>
              </div>
            </div>)}
        </div>

        <div className="mt-16 pt-12 border-t border-slate-800 text-center" data-id="element-227">
          <p className="text-slate-400 italic font-medium" data-id="element-228">
            "Atomic Team Design is a way of thinking about organisations through
            the lens of capability. It helps you reason about what you have,
            what you need, and where the gaps are."
          </p>
        </div>
      </div>
    </section>;
}